"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { useToast } from "@/components/feedback";
import { nextAcknowledgeDelay } from "@/core/flow/submit-result";
import { onTaskSubmit } from "@/core/flow/submit-task";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import { ProcessType, TaskType } from "@/types";

interface UseAcknowledgeTaskArgs {
	taskId: string;
	taskType: TaskType;
	processType: ProcessType;
	/** False where there is no FLOW to acknowledge against. */
	enabled?: boolean;
	onSettled?: (succeeded: boolean) => void;
}

const wait = (ms: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Completes a task the customer never acts on, behind a success screen.
 * Retries quietly and only warns once every attempt has failed.
 */
export const useAcknowledgeTask = ({
	taskId,
	taskType,
	processType,
	enabled = true,
	onSettled,
}: UseAcknowledgeTaskArgs) => {
	const { flowId } = useParams<{ flowId: string }>();
	const t = useTranslations("common.feedback");
	const { showToast } = useToast();
	const startedForRef = useRef<string | null>(null);
	const cancelledRef = useRef(false);
	const latestRef = useRef({ onSettled, showToast, t });

	useEffect(() => {
		latestRef.current = { onSettled, showToast, t };
	});

	useEffect(() => {
		// Strict Mode re-runs this effect; the guard keeps one attempt loop alive.
		cancelledRef.current = false;
		const cancel = () => {
			cancelledRef.current = true;
		};
		if (!enabled || !flowId || !taskId || startedForRef.current === taskId) {
			return cancel;
		}
		startedForRef.current = taskId;

		const acknowledge = async () => {
			for (let attempt = 0; ; attempt += 1) {
				const result = await onTaskSubmit({
					taskId,
					taskType,
					processType,
					body: {},
					flowId,
				});
				if (cancelledRef.current) return;
				if (result.succeeded) {
					latestRef.current.onSettled?.(true);
					return;
				}

				const delay =
					result.reason === "failed"
						? nextAcknowledgeDelay(attempt)
						: undefined;
				if (delay === undefined) break;
				await wait(delay);
				if (cancelledRef.current) return;
			}

			void sendClientLog({
				level: "warn",
				event: LogEvent.TaskSubmitAbandoned,
				outcome: "failure",
				message: "Background task acknowledgement gave up",
				task: taskType,
				processType,
				flowId,
			});
			const {
				showToast: show,
				t: translate,
				onSettled: settled,
			} = latestRef.current;
			show({ severity: "warning", message: translate("acknowledgeFailed") });
			settled?.(false);
		};
		void acknowledge();

		return cancel;
	}, [enabled, flowId, processType, taskId, taskType]);
};
